import React, { useEffect, useState } from "react";
import "./lead-reports.scss";

import { Box, Tab, Tabs } from "@mui/material";
import {
  AlertComponent,
  BreadcrumbComponent,
} from "../../../common/components";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { Form, Formik } from "formik";
import { getLeadReports, exportLeadReports } from "../../../api/lead-reports";
import CustomRadioGroup from "../../../common/components/radio-button-component/radio-button-component";
import OnboardedDateFilter from "../../tenant/tenant-list/tenant-filter-options/onboardeddate-filter";
import ActiveUsers from "./active-users/active-users";
import { useSetLoading } from "../../../common/hooks/appearance";
import { leadStatusesName } from "./lead-source-breakdown/lead-source-breakdown.constant";
import LeadConversationTrend from "./lead-conversation-trend/lead-conversation-trend";
import LeadSourceBreakdown from "./lead-source-breakdown/lead-source-breakdown";
import { useLogin } from "../../../common/hooks/useUser";
import CompanyReportFilter from "../../../common/components/company-report-filter/company-report-filter";
import partnerReportEmptyIcon from "../../../assets/icons/partner-reports-default.svg";
import EmailSetUpBanner from "../../../common/components/email-setup-required-banner/email-setup-banner";
import { useResourcePermissions } from "../../../common/hooks/resource-permissions";
import MODULES from "../../../constants/modules";

const crumbMapping = [
  {
    label: "Lead Management",
    link: "#",
    handleClick: () => {},
  },
  { label: "Lead Reports", link: "#" },
];

const LeadReports = () => {
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [reportsData, setReportsData] = useState<any>(null);
  const [leadsByStatusAttributionCount, setLeadsByStatusAttributionCount] =
    useState<any>([]);
  const [filterDate, setFilterDate] = useState<any>({});
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [indexValue, setIndexValue] = React.useState(0);
  const [partnerId, setPartnerId] = useState<number | null>(null);
  const [partnerName, setPartnerName] = useState<string | null>(null);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setIndexValue(newValue);
    if (newValue === 0) {
      setPartnerId(null);
    }
  };
  const loginDetails = useLogin();
  const tenantDetails = loginDetails?.tenantDetails;
  const userDetails = loginDetails?.userDetails;
  const isAFPSuperAdmin =
    tenantDetails?.tenantType === "SuperTenant" &&
    userDetails?.roles?.role === "Super Admin";
  const isEmailConfigured = loginDetails?.isEmailConfigured;
  const isSuperAdmin = userDetails?.roles?.role === "Super Admin";
  const { canExport } = useResourcePermissions(MODULES.EXPORT);

  const renderTabContent = () => {
    switch (indexValue) {
      case 0:
        return <>{renderReports()}</>;
      case 1:
        return (
          <>
            <div className="partner-report-company-filter">
              <CompanyReportFilter
                setCompany={(partner) => {
                  setPartnerId(partner?.id);
                  setPartnerName(partner?.name);
                }}
              />
            </div>
            {partnerId ? (
              <>{renderReports()}</>
            ) : (
              <Box className="partner-report-empty-state">
                <img src={partnerReportEmptyIcon} alt="No Partner Selected" />
              </Box>
            )}
          </>
        );
      default:
        return <>{renderReports()}</>;
    }
  };

  const renderReports = () => {
    return (
      <>
        <Box className="active-performance-wrapper" gap={1}>
          <ActiveUsers
            series={leadsByStatusAttributionCount?.percentage}
            handleAddLeadReports={handleAddLeadReports}
            title="Lead Status Attribution"
            loading={loading}
            seriesCount={leadsByStatusAttributionCount?.count}
            enableExport={canExport}
          />
          <LeadConversationTrend
            leadsConversionRate={reportsData?.leadsConversionRate}
            handleAddLeadReports={handleAddLeadReports}
            enableExport={canExport}
          />
        </Box>

        <LeadSourceBreakdown
          leadsBySourceBreakdown={reportsData?.leadsBySourceBreakdown}
          handleAddLeadReports={handleAddLeadReports}
          partnerId={partnerId}
          enableExport={canExport}
        />
      </>
    );
  };

  const fetchLeadReports = async () => {
    setLoading(true);
    try {
      const response = await getLeadReports({
        startDate: filterDate?.start,
        endDate: filterDate?.end,
        partnerId,
      });
      setReportsData(response?.data?.data);
      setLeadsByStatusAttributionCount({
        percentage:
          leadStatusesName?.map((status: any) => {
            return (
              response?.data?.data?.leadsByStatusAttribution?.find(
                (item: any) => item.status === status
              )?.percentage || 0
            );
          }) || [],
        count:
          leadStatusesName?.map((status: any) => {
            return (
              response?.data?.data?.leadsByStatusAttribution?.find(
                (item: any) => item.status === status
              )?.count || 0
            );
          }) || [],
      });
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch campaigns"
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAddLeadReports = async (data: any, params: any) => {
    setLoading(true);
    try {
      await exportLeadReports(data, { ...params, partnerId });
      setAlertMessage(
        partnerName
          ? `${partnerName} report generated successfully.`
          : "Report generated successfully."
      );
      setAlertType("success");
      setAlertOpen(true);
    } catch (error: any) {
      setAlertMessage(error?.response?.data?.message || "Failed to fetch");
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const shouldFetch =
      (!filterDate.start && !filterDate.end) ||
      (filterDate.start && filterDate.end);

    if (shouldFetch) {
      fetchLeadReports();
    }
  }, [filterDate, partnerId]);

  useSetLoading(loading);

  return (
    <Box className="user-reports-container lead-reports-wrapper">
      <Box>
        <BreadcrumbComponent breadcrumbLink={crumbMapping} />
      </Box>
      <Box className="user-reports-content">
        <Box className="user-reports-filter">
          <Formik
            initialValues={{
              filterItem: "Yes",
              onBoardedAtStart: null,
              onBoardedAtEnd: null,
            }}
            enableReinitialize
            // validate={validate}
            onSubmit={() => {}}
          >
            {({
              values,
              errors,
              touched,
              setFieldValue,
              handleSubmit,
              resetForm,
            }) => {
              return (
                <Box className="user-reports-filter-item" gap={3}>
                  <p>Date</p>
                  <Box
                    className="filter-value"
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                  >
                    {values?.filterItem === "Yes"
                      ? "Last 30 days "
                      : "Date range"}
                    {isFilterOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                    {isFilterOpen && (
                      <Box
                        className="filter-dropdown"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Form
                          className="drawer-content"
                          onSubmit={handleSubmit}
                        >
                          <Box>
                            <CustomRadioGroup
                              name="filterItem"
                              value={values?.filterItem}
                              options={[
                                { label: "Last 30 days", value: "Yes" },
                                { label: "Date range", value: "No" },
                              ]}
                              onChange={(e) => {
                                if (e.target.value === "Yes") {
                                  setFilterDate({});
                                  setFieldValue("onBoardedAtStart", null);
                                  setFieldValue("onBoardedAtEnd", null);
                                }
                                setFieldValue("filterItem", e.target.value);
                              }}
                            />
                            {values?.filterItem === "No" && (
                              <OnboardedDateFilter
                                startDate={values.onBoardedAtStart}
                                endDate={values.onBoardedAtEnd}
                                onDateRangeChange={(start, end) => {
                                  setFilterDate({
                                    start: start ? new Date(start) : null,
                                    end: end ? new Date(end) : null,
                                  });
                                  if (start)
                                    setFieldValue?.("onBoardedAtStart", start);
                                  if (end)
                                    setFieldValue?.("onBoardedAtEnd", end);
                                }}
                                startDateError={
                                  touched?.onBoardedAtStart &&
                                  errors?.onBoardedAtStart
                                    ? String(errors.onBoardedAtStart)
                                    : undefined
                                }
                                endDateError={
                                  touched?.onBoardedAtEnd &&
                                  errors?.onBoardedAtEnd
                                    ? String(errors.onBoardedAtEnd)
                                    : undefined
                                }
                              />
                            )}
                          </Box>
                        </Form>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            }}
          </Formik>
        </Box>
        {isAFPSuperAdmin ? (
          <>
            <Box>
              <Tabs
                value={indexValue}
                onChange={handleTabChange}
                allowScrollButtonsMobile
                className="lead-reports-tabs"
                sx={{
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 500,
                  },
                  "& .MuiTab-root.Mui-selected": {
                    color: "black",
                    borderBottom: "2px solid #785607",
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#785607",
                  },
                }}
              >
                <Tab iconPosition="start" label="AFP Reports" />
                <Tab iconPosition="start" label="Partner Reports" />
              </Tabs>
            </Box>
            {!isEmailConfigured && isSuperAdmin && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <EmailSetUpBanner />
              </div>
            )}
            <Box>{renderTabContent()}</Box>
          </>
        ) : (
          <>
            {!isEmailConfigured && isSuperAdmin && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <EmailSetUpBanner />
              </div>
            )}
            <Box>{renderReports()}</Box>
          </>
        )}
      </Box>
      <AlertComponent
        message={alertMessage}
        type={alertType ?? "info"}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </Box>
  );
};

export default LeadReports;
